import { FC, MouseEvent } from 'react'
import {
  ListItemButton,
  listItemButtonClasses,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material'

import {
  NAV_ACTIVE_FOREGROUND,
  NAV_ACTIVE_PILL_GRADIENT,
  NAV_DISABLED_TEXT,
  NAV_HOVER_BG,
  NAV_ICON,
  NAV_ITEM_RADIUS_PX,
  NAV_RAIL_ITEM_WIDTH_PX,
} from '@/common/constants'
import { ICON_MAP } from '@/components/componentLibrary/Icon/Icons'

/** Marks a header that opens the sub-page flyout. `CurbsNav` reads it on outside clicks. */
export const FLYOUT_TRIGGER_ATTRIBUTE = 'data-nav-flyout-trigger'

// Size of the chevron that marks a group in the collapsed rail. Smaller than the
// open drawer's chevron, because it has to fit in the pill beside the icon.
const RAIL_CHEVRON_SIZE_PX = 16
// The header's hover and focus styles use this class to show the rail chevron.
const RAIL_GROUP_MARK_CLASS = 'curb-nav-group-mark'

interface CurbsNavSectionHeaderProps {
  drawerOpen: boolean
  icon: any
  title: string
  testId?: string
  disabled?: boolean
  /** Paints the active pill, for a header whose group holds the open page. */
  active?: boolean
  /** Whether the content this header controls is showing. */
  expanded: boolean
  onClick: (event: MouseEvent<HTMLElement>) => void
  /** Id of the element this header shows and hides, for `aria-controls`. */
  controls?: string
  /** The header opens the sub-page flyout beside the drawer, not a list below it. */
  flyoutTrigger?: boolean
}

/**
 * The clickable row of a nav item that has content of its own: a collapsible
 * section, or a group whose sub-pages open in the flyout.
 */
const CurbsNavSectionHeader: FC<CurbsNavSectionHeaderProps> = ({
  drawerOpen,
  icon,
  title,
  testId,
  disabled,
  active,
  expanded,
  onClick,
  controls,
  flyoutTrigger,
}) => {
  /*
    A group holding the open page carries the same pill as any other top-level
    item, open or collapsed. The selected sub-page is marked a different way —
    see `CurbsNavDrawerItem`'s nested branch — so the two do not read as two
    selections.
  */
  const iconColor = () => {
    if (disabled) return NAV_DISABLED_TEXT
    return active ? NAV_ACTIVE_FOREGROUND : NAV_ICON
  }

  const labelColor = () => {
    if (disabled) return NAV_DISABLED_TEXT
    return active ? NAV_ACTIVE_FOREGROUND : 'common.white'
  }

  // While a group's flyout is open, its header keeps the hover tint, so it is
  // clear which group the flyout belongs to.
  const background = () => {
    if (active) return NAV_ACTIVE_PILL_GRADIENT
    return flyoutTrigger && expanded ? NAV_HOVER_BG : 'none'
  }

  /*
    The button is the pill itself, the same as a plain item in
    `CurbsNavDrawerItem`. If the button filled the row with the pill inside it,
    MUI's own hover, focus and ripple would paint the full width of the drawer.
  */
  const header = (
    <ListItemButton
      onClick={event => {
        if (!disabled) onClick(event)
      }}
      aria-expanded={expanded}
      aria-controls={expanded ? controls : undefined}
      aria-disabled={disabled || undefined}
      aria-label={drawerOpen ? undefined : title}
      data-testid={testId ? `curb-nav-section-${testId}` : undefined}
      {...(flyoutTrigger ? { [FLYOUT_TRIGGER_ATTRIBUTE]: '' } : {})}
      sx={[
        {
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          height: '40px',
          borderRadius: `${NAV_ITEM_RADIUS_PX}px`,
          background: background(),
          '&:hover': { backgroundColor: active ? undefined : NAV_HOVER_BG },
          // Keyboard focus shows the rail's group mark the same as hover does.
          [`&:hover .${RAIL_GROUP_MARK_CLASS}, &.${listItemButtonClasses.focusVisible} .${RAIL_GROUP_MARK_CLASS}`]:
            { opacity: 1 },
        },
        drawerOpen
          ? { mx: 2, px: 1.5 }
          : {
              width: `${NAV_RAIL_ITEM_WIDTH_PX}px`,
              mx: 'auto',
              px: 0,
              justifyContent: 'center',
            },
      ]}
    >
      <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center', color: iconColor() }}>
        {icon}
      </ListItemIcon>
      {drawerOpen && (
        <ListItemText
          primary={title}
          slotProps={{
            primary: { sx: { color: labelColor(), fontWeight: 600 } },
          }}
        />
      )}
      {/* Open, a group shows a chevron that points to the side the flyout opens on. */}
      {drawerOpen && flyoutTrigger && (
        <ICON_MAP.chevronRight sx={{ fontSize: 20, color: iconColor() }} />
      )}
      {/*
        Collapsed there is no label, so a group would look the same as a plain
        page link. A smaller chevron at the pill's right edge marks it. It is at
        the edge and not on the icon, so the cursor does not cover it while the
        user points at the icon.

        It shows on hover and keyboard focus, so the rail stays clean at rest.
        Like the hover tint, it also stays visible while this group's flyout is
        open. It stays mounted and fades in, rather than mounting on hover, so
        it does not flash in and out.

        Placed absolutely, so the icon stays centered and lines up with the
        plain items above and below. The glyph has blank space on each side of
        its box, so the box can run a little past the pill's edge.
      */}
      {!drawerOpen && flyoutTrigger && (
        <ICON_MAP.chevronRight
          className={RAIL_GROUP_MARK_CLASS}
          data-testid="curb-nav-group-mark"
          sx={theme => ({
            position: 'absolute',
            right: '-2px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: RAIL_CHEVRON_SIZE_PX,
            color: iconColor(),
            opacity: expanded ? 1 : 0,
            transition: theme.transitions.create('opacity', {
              duration: theme.transitions.duration.shorter,
            }),
          })}
        />
      )}
    </ListItemButton>
  )

  // Collapsed there is no label, so the name has to come from a tooltip.
  return drawerOpen ? (
    header
  ) : (
    <Tooltip title={title} placement="right">
      {header}
    </Tooltip>
  )
}

export default CurbsNavSectionHeader
